import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select option…',
  required = false,
  className = '',
  error = false,
  style = {},
  compact = false   // smaller font + height to match dense grid inputs
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef    = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const updateCoords = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateCoords();
      window.addEventListener('resize', updateCoords);
      window.addEventListener('scroll', updateCoords, true);
    }
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  const selectedOption  = options.find(o => String(o.value !== undefined ? o.value : o.key) === String(value));
  const filteredOptions = options.filter(o =>
    String(o.label || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (val) => { onChange(val); setIsOpen(false); };
  const handleClear  = (e) => { e.stopPropagation(); onChange(''); };

  return (
    <div
      ref={containerRef}
      className={`sss-wrap ${className}`}
      style={{ position: 'relative', width: '100%', ...style }}
    >
      {/* Trigger */}
      <div
        className={`sss-trigger${isOpen ? ' open' : ''}${error ? ' error' : ''}`}
        onClick={() => setIsOpen(o => !o)}
        role="combobox"
        aria-expanded={isOpen}
        style={compact ? { minHeight: 34, padding: '4px 8px 4px 10px' } : undefined}
      >
        <span
          className={`sss-value${!selectedOption ? ' placeholder' : ''}`}
          style={{
            ...(selectedOption?.color ? { color: selectedOption.color, fontWeight: 600 } : {}),
            ...(compact ? { fontSize: 12.5 } : {})
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="sss-icons">
          {selectedOption && (
            <span
              className="sss-clear"
              onClick={handleClear}
              title="Clear"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={15} className={`sss-chevron${isOpen ? ' up' : ''}`} />
        </div>
      </div>

      {/* Required hidden input */}
      {required && (
        <input
          tabIndex={-1} required value={value || ''} onChange={() => {}}
          style={{ opacity: 0, width: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
        />
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          className="sss-dropdown"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            minWidth: coords.width,
            zIndex: 99999
          }}
        >
          {/* Search */}
          <div className="sss-search">
            <Search size={13} className="sss-search-icon" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="sss-search-input"
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <X size={13} className="sss-search-clear" onClick={e => { e.stopPropagation(); setSearch(''); }} />
            )}
          </div>

          {/* Options */}
          <div className="sss-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const optVal = opt.value !== undefined ? opt.value : opt.key;
                const optKey = opt.key ?? opt.value ?? `opt-${idx}`;
                const isSelected = String(optVal) === String(value);
                return (
                  <div
                    key={optKey}
                    className={`sss-option${isSelected ? ' selected' : ''}`}
                    onClick={() => handleSelect(optVal)}
                  >
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap', 
                      flex: 1, 
                      textAlign: 'left',
                      ...(opt.color ? { color: opt.color, fontWeight: 600 } : {})
                    }}>
                      {opt.label}
                    </span>
                    {isSelected && <Check size={13} className="sss-check" style={{ flexShrink: 0, ...(opt.color ? { color: opt.color } : {}) }} />}
                  </div>
                );
              })
            ) : (
              <div className="sss-empty">No options found</div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .sss-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-height: 37px;
          padding: 6px 10px 6px 12px;
          border: 1.5px solid var(--border-2);
          border-radius: var(--radius-sm);
          background: var(--surface);
          cursor: pointer;
          user-select: none;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
          font-family: var(--font);
        }
        .sss-trigger:hover { border-color: var(--gray-400); }
        .sss-trigger.open {
          border-color: var(--primary-500);
          box-shadow: 0 0 0 3px rgba(42,80,128,0.12);
        }
        .sss-trigger.error { border-color: var(--danger); }
        .sss-trigger.error.open { box-shadow: 0 0 0 3px rgba(239,68,68,0.12); }

        .sss-value {
          font-size: 0.9rem;
          color: var(--text-primary);
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }
        .sss-value.placeholder { color: var(--text-muted); font-weight: 400; }

        .sss-icons { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }

        .sss-clear {
          display: flex; align-items: center; justify-content: center;
          width: 18px; height: 18px;
          border-radius: 50%;
          color: var(--text-muted);
          cursor: pointer;
          transition: background var(--transition-fast), color var(--transition-fast);
        }
        .sss-clear:hover { background: var(--gray-200); color: var(--text-primary); }

        .sss-chevron {
          color: var(--text-muted);
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }
        .sss-chevron.up { transform: rotate(180deg); }

        /* Dropdown panel */
        .sss-dropdown {
          position: absolute;
          top: calc(100% + 5px);
          left: 0;
          min-width: 100%;
          width: max-content;
          max-width: 280px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-md);
          z-index: 9999;
          overflow: hidden;
          animation: modalIn 0.12s ease;
          text-align: left;
          direction: ltr;
        }

        /* Search row */
        .sss-search {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 10px;
          border-bottom: 1px solid var(--border);
          background: var(--surface-2);
          text-align: left;
        }
        .sss-search-icon { color: var(--text-muted); flex-shrink: 0; }
        .sss-search-input {
          flex: 1; min-width: 0; border: none; outline: none; background: transparent;
          font-size: 0.82rem; font-family: var(--font); color: var(--text-primary);
        }
        .sss-search-clear { color: var(--text-muted); cursor: pointer; }
        .sss-search-clear:hover { color: var(--text-primary); }

        /* Options list */
        .sss-list { max-height: 220px; overflow-y: auto; padding: 4px 0; }
        .sss-list::-webkit-scrollbar { width: 3px; }
        .sss-list::-webkit-scrollbar-thumb { background: var(--gray-300); border-radius: 99px; }

        .sss-option {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px;
          padding: 9px 12px;
          font-size: 0.86rem;
          color: var(--text-primary);
          cursor: pointer;
          transition: background var(--transition-fast);
          font-family: var(--font);
          white-space: nowrap;
          text-align: left;
        }
        .sss-option:hover { background: var(--gray-50); }
        .sss-option.selected { background: var(--primary-50); color: var(--primary-700); font-weight: 600; }

        .sss-check { color: var(--primary-600); flex-shrink: 0; }

        .sss-empty {
          padding: 14px 12px;
          font-size: 0.82rem;
          color: var(--text-muted);
          text-align: center;
        }
      `}</style>
    </div>
  );
}
